import { useCallback, useEffect, useState } from "react";
import type { Key } from "react-aria-components";

import { trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import { Modal } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import type { OrgDetail } from "../../types";
import { useAdminGridOptions } from "../../utils/gridState";
import { AdminGrid } from "../AdminGrid";
import { TabLayout } from "../TabLayout";

export function ClientClientsTable({ org }: { org: OrgDetail }) {
  const orgs = org.additionalOrgs;

  const updateAdditionalOrg = trpc.admin.org.updateAdditionalOrg.useMutation(
    mutationResponse({
      errorTitle: "Failed to update client",
      successMessage: "Client updated",
    }),
  );
  const updateOrder = trpc.admin.org.updateOrgsOrder.useMutation();

  const { options } = useAdminGridOptions(
    orgs,
    [
      {
        field: "additionalOrg.name",
        headerName: "Client",
        rowDrag: true,
        cellRenderer: (params) => (
          <Link to="/admin/clients/$id/edit" params={{ id: params.id }}>
            {params.name}
          </Link>
        ),
        cellRendererParams: (params) => ({
          id: params.data.additionalOrg.id,
          name: params.data.additionalOrg.name,
        }),
      },
      {
        field: "allowSharing",
        editable: !updateAdditionalOrg.isPending,
        cellRenderer: "agCheckboxCellRenderer",
        cellEditor: "agCheckboxCellEditor",
        onCellValueChanged(event) {
          updateAdditionalOrg.mutate({
            orgId: event.data.orgId,
            additionalOrgId: event.data.additionalOrg.id,
            allowSharing: event.newValue,
            allowSplitBilling: event.data.allowSplitBilling,
            allowBillTo: event.data.allowBillTo,
          });
        },
      },
      {
        field: "allowSplitBilling",
        editable: !updateAdditionalOrg.isPending,
        cellRenderer: "agCheckboxCellRenderer",
        cellEditor: "agCheckboxCellEditor",
        onCellValueChanged(event) {
          updateAdditionalOrg.mutate({
            orgId: event.data.orgId,
            additionalOrgId: event.data.additionalOrg.id,
            allowSharing: event.data.allowSharing,
            allowSplitBilling: event.newValue,
            allowBillTo: event.data.allowBillTo,
          });
        },
      },
      {
        field: "allowBillTo",
        headerName: "Allow Bill To",
        editable: !updateAdditionalOrg.isPending,
        cellRenderer: "agCheckboxCellRenderer",
        cellEditor: "agCheckboxCellEditor",
        onCellValueChanged(event) {
          updateAdditionalOrg.mutate({
            orgId: event.data.orgId,
            additionalOrgId: event.data.additionalOrg.id,
            allowSharing: event.data.allowSharing,
            allowSplitBilling: event.data.allowSplitBilling,
            allowBillTo: event.newValue,
          });
        },
      },
    ],
    {
      sideBar: undefined,
      domLayout: "autoHeight",
      getRowId: (row) => row.data.additionalOrg.id.toString(),
      onRowDragEnd: (e) => {
        const ids: number[] = [];
        e.api.forEachNode((node) => {
          if (node.data?.additionalOrg.id) {
            ids.push(node.data.additionalOrg.id);
          }
        });
        updateOrder.mutate(
          { id: org.id, orgs: ids },
          mutationResponse({
            errorTitle: "Error reordering clients",
            successMessage: "Clients reordered",
          }),
        );
      },
    },
  );

  return (
    <TabLayout
      description="The following clients will be available to select for sending
    additional reports or split billing."
      actions={<UpdateClientsDialog org={org} />}
    >
      <AdminGrid {...options} />
    </TabLayout>
  );
}

function UpdateClientsDialog({ org }: { org: OrgDetail }) {
  const [open, setOpen] = useState(false);
  const [keys, setKeys] = useState<Key[]>(
    org.additionalOrgs.map(({ additionalOrg }) => additionalOrg.id),
  );
  const { data, isLoading } = trpc.orgs.list.useQuery();
  const items = data?.filter((d) => d.id !== org.id) || [];

  const updateOrgs = trpc.admin.org.updateOrgs.useMutation();

  useEffect(() => {
    if (open) {
      setKeys(org.additionalOrgs.map(({ additionalOrg }) => additionalOrg.id));
    }
  }, [open, org.additionalOrgs]);

  const onSelectionChange = useCallback(
    (keys: "all" | Set<Key>) => {
      if (keys === "all") {
        setKeys(data?.map((t) => t.id) || []);
      } else {
        setKeys(Array.from(keys));
      }
    },
    [data],
  );

  const onUpdate = useCallback(() => {
    updateOrgs.mutate(
      { id: org.id, orgs: keys.map((k) => Number(k)) },
      mutationResponse({
        errorTitle: "Failed to update clients",
        successMessage: "Clients updated",
        onSuccess: () => setOpen(false),
      }),
    );
  }, [keys, org.id, updateOrgs]);

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button variant="secondary" onPress={() => setOpen(true)}>
        Edit clients
      </Button>
      <Modal>
        <Dialog>
          <DialogHeader>
            <DialogTitle>Update clients</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <GridList
              aria-label="Select clients"
              items={items}
              selectionMode="multiple"
              selectedKeys={keys}
              onSelectionChange={onSelectionChange}
            >
              {(test) => (
                <GridListItem key={test.id} textValue={test.name}>
                  <Text>{test.name}</Text>
                </GridListItem>
              )}
            </GridList>
          </DialogBody>
          <DialogFooter>
            <Button isPending={updateOrgs.isPending} onPress={onUpdate}>
              Update
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}
